import { Info } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NetworkAuditLogs__AuditLog as AuditLog } from '~/components/AuditLogs/queries/list.generated';
import { ActionButton } from '~/components/button';
import { formatDate } from '~/lib/datetime';

export const useColumns = (handleViewDetails: (row: AuditLog) => void): GridColDef<AuditLog>[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: t('pages.audit_logs.columns.date_time'),
        sortable: false,
        renderCell: ({ row }) => {
          return formatDate(row.createdAt);
        },
        flex: 0.2,
        minWidth: 250,
      },
      {
        field: 'userName',
        headerName: t('pages.audit_logs.columns.user'),
        sortable: false,
        renderCell: ({ row }) => {
          return row.userName || t('pages.audit_logs.columns.deleted_user');
        },
        flex: 0.2,
      },
      {
        field: 'actionName',
        headerName: t('common.labels.action'),
        sortable: false,
        flex: 0.25,
      },
      {
        field: 'recordsCount',
        headerName: t('pages.audit_logs.columns.records'),
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          return <Chip color="primary" label={row.recordsCount} />;
        },
        minWidth: 120,
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        minWidth: isSmallAndUp ? 150 : 0,
        getActions: ({ row }) => {
          return [
            <ActionButton
              key={`view-${row.id}`}
              title={t('common.labels.details')}
              Icon={Info}
              onClick={() => handleViewDetails(row)}
            />,
          ];
        },
      },
    ],
    [isSmallAndUp, handleViewDetails, t],
  );
};
